import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Copy,
  ExternalLink,
} from "lucide-react";
import { toast } from "react-toastify";
import { colors, borderRadiusPx } from "../../../../../theme";
import { CommonDialog, CommonButton } from "../../../../Components/ui";
import { getNotificationConfig } from "./NotificationCard";

const NotificationDetailModal = ({
  open,
  onClose,
  notification,
  onNavigate,
  formatTimeAgo,
  formatExactDate,
}) => {
  if (!notification) return null;

  const config = getNotificationConfig(notification.type);
  const IconComponent = config.icon;
  const timeAgo = formatTimeAgo ? formatTimeAgo(notification.createdOn) : "Recently";
  const exactDate = formatExactDate ? formatExactDate(notification.createdOn) : "";

  const handleCopy = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(String(text));
    toast.success(`${label} copied to clipboard!`, {
      position: "top-right",
      autoClose: 2000,
    });
  };

  return (
    <CommonDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title="Notification Details"
      subtitle="Complete system event & on-chain metadata"
      actions={
        <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ width: "100%" }}>
          <CommonButton variant="outline" size="small" onClick={onClose}>
            Close
          </CommonButton>
          <CommonButton
            variant="primary"
            size="small"
            onClick={() => {
              onClose();
              if (onNavigate) onNavigate(notification);
            }}
            endIcon={<ExternalLink size={15} />}
          >
            {config.actionLabel}
          </CommonButton>
        </Stack>
      }
    >
      <Stack spacing={2.5}>
        {/* Header Block with Category Badge */}
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: borderRadiusPx.lg,
              bgcolor: config.iconBg,
              color: config.iconColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <IconComponent size={24} />
          </Box>
          <Box>
            <Box
              sx={{
                display: "inline-block",
                px: 1.25,
                py: 0.25,
                borderRadius: borderRadiusPx.sm,
                bgcolor: config.iconBg,
                color: config.iconColor,
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                mb: 0.5,
              }}
            >
              {config.badgeLabel}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: colors.slate[900], fontSize: "1.05rem" }}>
              {notification.title}
            </Typography>
          </Box>
        </Stack>

        {/* Message Content Box */}
        <Box
          sx={{
            p: 2,
            borderRadius: borderRadiusPx.lg,
            bgcolor: colors.slate[50],
            border: `1px solid ${colors.slate[200]}`,
          }}
        >
          <Typography variant="caption" sx={{ color: colors.slate[500], fontWeight: 700, textTransform: "uppercase", display: "block", mb: 0.5 }}>
            Message Content
          </Typography>
          <Typography variant="body2" sx={{ color: colors.slate[800], lineHeight: 1.6 }}>
            {notification.content}
          </Typography>
        </Box>

        {/* Metadata Details Grid */}
        <Box sx={{ border: `1px solid ${colors.slate[200]}`, borderRadius: borderRadiusPx.lg, overflow: "hidden" }}>
          {/* Row 1: Event Type */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: 1.5, bgcolor: colors.background.paper, borderBottom: `1px solid ${colors.slate[100]}` }}
          >
            <Typography variant="caption" sx={{ color: colors.slate[600], fontWeight: 600 }}>
              Event Type
            </Typography>
            <Typography variant="caption" sx={{ color: colors.slate[900], fontWeight: 700 }}>
              {notification.type || "SystemNotification"}
            </Typography>
          </Stack>

          {/* Row 2: Reference ID */}
          {notification.genId && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ p: 1.5, bgcolor: colors.slate[50], borderBottom: `1px solid ${colors.slate[100]}` }}
            >
              <Typography variant="caption" sx={{ color: colors.slate[600], fontWeight: 600 }}>
                Reference ID
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="caption" sx={{ fontFamily: "monospace", color: colors.slate[900], fontWeight: 700 }}>
                  {String(notification.genId)}
                </Typography>
                <Tooltip title="Copy ID">
                  <IconButton size="small" onClick={() => handleCopy(notification.genId, "Reference ID")} sx={{ p: 0.25 }}>
                    <Copy size={13} color={colors.slate[500]} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          )}

          {/* Row 3: Lottery Name */}
          {notification.lotteryName && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ p: 1.5, bgcolor: colors.background.paper, borderBottom: `1px solid ${colors.slate[100]}` }}
            >
              <Typography variant="caption" sx={{ color: colors.slate[600], fontWeight: 600 }}>
                Game / Lottery Mode
              </Typography>
              <Typography variant="caption" sx={{ color: colors.primary.main, fontWeight: 700 }}>
                {notification.lotteryName} BET
              </Typography>
            </Stack>
          )}

          {/* Row 4: Owner / Creator Address */}
          {notification.owner && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ p: 1.5, bgcolor: colors.slate[50], borderBottom: `1px solid ${colors.slate[100]}` }}
            >
              <Typography variant="caption" sx={{ color: colors.slate[600], fontWeight: 600 }}>
                Owner Address
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="caption" sx={{ fontFamily: "monospace", color: colors.slate[900] }}>
                  {`${notification.owner.slice(0, 8)}...${notification.owner.slice(-6)}`}
                </Typography>
                <Tooltip title="Copy Address">
                  <IconButton size="small" onClick={() => handleCopy(notification.owner, "Owner address")} sx={{ p: 0.25 }}>
                    <Copy size={13} color={colors.slate[500]} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          )}

          {/* Row 5: User Address */}
          {notification.user && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ p: 1.5, bgcolor: colors.background.paper, borderBottom: `1px solid ${colors.slate[100]}` }}
            >
              <Typography variant="caption" sx={{ color: colors.slate[600], fontWeight: 600 }}>
                Participant Address
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="caption" sx={{ fontFamily: "monospace", color: colors.slate[900] }}>
                  {`${notification.user.slice(0, 8)}...${notification.user.slice(-6)}`}
                </Typography>
                <Tooltip title="Copy Address">
                  <IconButton size="small" onClick={() => handleCopy(notification.user, "User address")} sx={{ p: 0.25 }}>
                    <Copy size={13} color={colors.slate[500]} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          )}

          {/* Row 6: Timestamp */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: 1.5, bgcolor: colors.slate[50] }}
          >
            <Typography variant="caption" sx={{ color: colors.slate[600], fontWeight: 600 }}>
              Recorded On
            </Typography>
            <Typography variant="caption" sx={{ color: colors.slate[900], fontWeight: 600 }}>
              {exactDate || timeAgo} ({timeAgo})
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </CommonDialog>
  );
};

NotificationDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  notification: PropTypes.object,
  onNavigate: PropTypes.func,
  formatTimeAgo: PropTypes.func,
  formatExactDate: PropTypes.func,
};

export default NotificationDetailModal;
